import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberIntimacyTypes.module.scss";
import { INTIMACY_TYPES } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (q10) */
import mrImg from "@/assets/UI/memberCreate/q10Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q10Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q10MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q10MrsM.webp";

/* ---- Framer animations (same feel as Attraction/Gender) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Outlet context hook */
function useMemberLayoutContext() {
  return useOutletContext();
}

/* Simple emoji per intimacy option */
const INTIMACY_META = {
  kissing_touching: { emoji: "💋" },
  oral_pleasure: { emoji: "🔥" },
  slow_sensual_lovemaking: { emoji: "🧿" },
  rough_dominant_play: { emoji: "🕶️" },
  soft_emotional_connection: { emoji: "🧸" },
  roleplay_fantasies: { emoji: "🎭" },
  kinky_fetish_play: { emoji: "🧷" },
};

const INTIMACY_LABELS = {
  kissing_touching: "Kissing, touching, teasing",
  oral_pleasure: "Oral pleasure",
  slow_sensual_lovemaking: "Slow, sensual love-making",
  rough_dominant_play: "Rough, dominant play",
  soft_emotional_connection: "Soft, emotional connection",
  roleplay_fantasies: "Roleplay and fantasies",
  kinky_fetish_play: "Kinky or fetish-based play",
};

function toggleChoice(current, value) {
  if (current.includes(value)) return current.filter((v) => v !== value);
  return [...current, value];
}

function getClosingKey(rawGender, isCouple) {
  if (isCouple) return "onboarding.intimacy_types.closing.couple";
  if (rawGender === "man" || rawGender === "trans_man")
    return "onboarding.intimacy_types.closing.man";
  if (rawGender === "woman" || rawGender === "trans_woman")
    return "onboarding.intimacy_types.closing.woman";
  return "onboarding.intimacy_types.closing.other";
}

function buildIntro(isCouple, rawGender, t) {
  if (isCouple) {
    return {
      introLines: [
        t("onboarding.intimacy_types.couple.intro1"),
        t("onboarding.intimacy_types.couple.intro2"),
        t("onboarding.intimacy_types.couple.intro3"),
      ],
      questionLines: [
        t("onboarding.intimacy_types.couple.question1"),
        t("onboarding.intimacy_types.couple.question2"),
        t("onboarding.intimacy_types.couple.question3"),
      ],
    };
  }

  if (rawGender === "man" || rawGender === "trans_man") {
    return {
      introLines: [
        t("onboarding.intimacy_types.single.man.intro1"),
        t("onboarding.intimacy_types.single.man.intro2"),
        t("onboarding.intimacy_types.single.man.intro3"),
        t("onboarding.intimacy_types.single.man.intro4"),
      ],
      questionLines: [],
    };
  }

  if (rawGender === "woman" || rawGender === "trans_woman") {
    return {
      introLines: [
        t("onboarding.intimacy_types.single.woman.intro1"),
        t("onboarding.intimacy_types.single.woman.intro2"),
        t("onboarding.intimacy_types.single.woman.intro3"),
        t("onboarding.intimacy_types.single.woman.intro4"),
        t("onboarding.intimacy_types.single.woman.intro5"),
      ],
      questionLines: [],
    };
  }

  return {
    introLines: [
      t("onboarding.intimacy_types.single.other.intro1"),
      t("onboarding.intimacy_types.single.other.intro2"),
      t("onboarding.intimacy_types.single.other.intro3"),
      t("onboarding.intimacy_types.single.other.intro4"),
    ],
    questionLines: [],
  };
}

export default function CreateMemberIntimacyTypes() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);
  const [memberChoices, setMemberChoices] = useState([]);
  const [partnerChoices, setPartnerChoices] = useState([]);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, fallback) =>
    i18n.t(k, fallback ? { defaultValue: fallback } : undefined);

  const title = t("onboarding.intimacy_types.title", "Intimacy vibes");

  const { introLines, questionLines } = useMemo(
    () => buildIntro(isCouple, rawGender, t),
    [isCouple, rawGender],
  );

  const guideTitle = t("onboarding.intimacy_types.guide.title", "Quick note");
  const guideBody = t(
    "onboarding.intimacy_types.guide.body",
    "This doesn’t lock you in. It just helps your feed feel more like your vibe.",
  );

  const memberBlockTitle = t(
    "onboarding.intimacy_types.member.legend",
    "Pick what you enjoy",
  );

  const partnerTurnLabel = t(
    "onboarding.intimacy_types.partner.turn",
    "Now it’s your partner’s turn…",
  );

  const partnerBlockTitle = t(
    "onboarding.intimacy_types.partner.legend",
    "Pick what your partner enjoys",
  );

  const isManLikeSingle =
    !isCouple && (rawGender === "man" || rawGender === "trans_man");

  const baseOptions =
    Array.isArray(INTIMACY_TYPES) && INTIMACY_TYPES.length
      ? INTIMACY_TYPES
      : Object.keys(INTIMACY_LABELS);

  const options = baseOptions.map((value) => {
    const emoji = INTIMACY_META[value]?.emoji || "💫";
    const defaultLabel = INTIMACY_LABELS[value] || value;

    let labelKey = `onboarding.intimacy_types.options.${value}.label`;
    if (value === "oral_pleasure" && isManLikeSingle) {
      labelKey = `onboarding.intimacy_types.options.${value}.label_man`;
    }

    return {
      value,
      emoji,
      title: t(labelKey, defaultLabel),
      hint: t(`onboarding.intimacy_types.tapToSelect.${value}`, {
        defaultValue: "Tap to add this vibe to your attraction mix",
      }),
    };
  });

  const hasMember = memberChoices.length > 0;
  const hasPartner = partnerChoices.length > 0;

  const closingKey =
    hasMember && (!isCouple || hasPartner)
      ? getClosingKey(rawGender, isCouple)
      : null;

  const closingText = closingKey ? t(closingKey, "") : "";

  const submitDisabled =
    submitting || !memberChoices.length || (isCouple && !partnerChoices.length);

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoices.length) {
      toast.info(
        t(
          "onboarding.intimacy_types.toast.member_required",
          "Select at least one intimacy vibe that you enjoy.",
        ),
      );
      return;
    }
    if (isCouple && !partnerChoices.length) {
      toast.info(
        t(
          "onboarding.intimacy_types.toast.partner_required",
          "Let your partner choose at least one intimacy vibe too.",
        ),
      );
      return;
    }

    const payload = isCouple
      ? {
          memberIntimacyTypes: memberChoices,
          partnerIntimacyTypes: partnerChoices,
        }
      : { memberIntimacyTypes: memberChoices };

    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/v1/member-create/intimacy_types",
        payload,
        {
          skipToast: true,
        },
      );

      await refreshMe();
      navigate(data?.next || "/member/sexual_fantasies", { replace: true });
    } catch (err) {
      console.error("update intimacy types failed:", err);
      toast.error(
        t(
          "onboarding.intimacy_types.toast.save_error",
          "We couldn’t save your intimacy vibes. Please try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function renderOptionsBlock({ name, subtitleText, choices, setChoices }) {
    return (
      <div className={cls.block}>
        {subtitleText ? (
          <div className={cls.blockTitle}>{subtitleText}</div>
        ) : null}

        <fieldset className={cls.options}>
          <legend className="sr-only">{subtitleText || title}</legend>

          {options.map((opt) => {
            const checked = choices.includes(opt.value);

            return (
              <label
                key={`${name}-${opt.value}`}
                className={`${cls.option} ${checked ? cls.optionActive : ""}`}
              >
                <input
                  type="checkbox"
                  name={name}
                  value={opt.value}
                  checked={checked}
                  onChange={() =>
                    setChoices((prev) => toggleChoice(prev, opt.value))
                  }
                  aria-label={opt.title}
                />

                <span className={cls.optionBody}>
                  <span className={cls.optionEmoji} aria-hidden="true">
                    {opt.emoji}
                  </span>

                  <span className={cls.optionText}>
                    <strong>{opt.title}</strong>
                    <small>{opt.hint}</small>
                  </span>

                  <span className={cls.optionDot} aria-hidden="true" />
                </span>
              </label>
            );
          })}
        </fieldset>
      </div>
    );
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>

              {introLines.map((line) => (
                <div key={line} className={cls.formSub}>
                  {line}
                </div>
              ))}

              {questionLines.map((line) => (
                <div key={line} className={cls.formSub}>
                  {line}
                </div>
              ))}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit} noValidate>
            {renderOptionsBlock({
              name: "memberIntimacyTypes",
              subtitleText: memberBlockTitle,
              choices: memberChoices,
              setChoices: setMemberChoices,
            })}

            {isCouple &&
              renderOptionsBlock({
                name: "partnerIntimacyTypes",
                subtitleText: `${partnerTurnLabel} ${partnerBlockTitle}`,
                choices: partnerChoices,
                setChoices: setPartnerChoices,
              })}

            <div className={cls.readNote}>
              <strong>💡 {guideTitle}</strong>
              <p>{guideBody}</p>
            </div>

            {closingText ? (
              <div className={cls.closingNote} aria-live="polite">
                {closingText.split("\n").map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            ) : null}

            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", "Saving…")
                  : t("common.continue", "Continue")}
              </button>
            </div>
          </form>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
